/**
 * apps/web :: pages :: WhatsNextPage
 *
 * Post-pass journey hub (ROADS-431).
 *
 * Renders 7 sections in journey-arc order:
 *   1. Get your physical licence       (per-state-licensing.ts; CA + TX live)
 *   2. Insurance 101                    (glossary + per-state minimums; ROADS-427)
 *   3. How to apply for insurance       (5-step practical walkthrough)
 *   4. Partner offers                   (renders null when none active)
 *   5. Vehicle registration + plates    (placeholder — ROADS-438 lands data)
 *   6. AAA membership                   (static editorial)
 *   7. Other ancillary                  (motor-voter / organ-donor / REAL ID)
 *
 * Soft-paywall: the route is gated by `checkFirstPassGate`. Until the
 * learner has a passed exam attempt on record (any of their target
 * states), the page renders a paywall card pointing back at /exam.
 *
 * Status-agnostic per ROADS-436: every section frames itself as a
 * state regime / public-fact reference, never as advice tied to a
 * specific applicant population.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import LearnerPortalShell from "@/components/learner/LearnerPortalShell";
import PhysicalLicenceSection from "@/components/whatsnext/PhysicalLicenceSection";
import Insurance101Section from "@/components/whatsnext/Insurance101Section";
import InsuranceApplyHowToSection from "@/components/whatsnext/InsuranceApplyHowToSection";
import PartnerOffersSection from "@/components/whatsnext/PartnerOffersSection";
import VehicleRegistrationSection from "@/components/whatsnext/VehicleRegistrationSection";
import AaaMembershipSection from "@/components/whatsnext/AaaMembershipSection";
import AncillarySection from "@/components/whatsnext/AncillarySection";
import { getProfile, type UserProfile } from "@/lib/profile-api";
import {
  checkFirstPassGate,
  type WhatsNextGateStatus,
} from "@/lib/whatsnext-gate";

type Lang = "en" | "es";

function pickLang(raw: string | undefined): Lang {
  const primary = (raw ?? "en").split("-")[0].toLowerCase();
  return primary === "es" ? "es" : "en";
}

export default function WhatsNextPage() {
  const { t, i18n } = useTranslation();
  const lang = pickLang(i18n.language);

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [gate, setGate] = useState<WhatsNextGateStatus>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const p = await getProfile();
        if (cancelled) return;
        setProfile(p);
        if (!p) {
          setGate({ kind: "blocked" });
          return;
        }
        const status = await checkFirstPassGate(p.target_states);
        if (cancelled) return;
        setGate(status);
      } catch (err) {
        if (!cancelled) {
          setGate({
            kind: "error",
            message: err instanceof Error ? err.message : "unknown",
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Resolve which state's content to surface in the per-state sections.
   * Priority:
   *   1. The state where the user actually passed (gate.passedStateCode).
   *   2. The user's `current_focus_state`.
   *   3. The first of `target_states`.
   *   4. Empty string — sections render their "coming soon" fallback.
   */
  const resolvedState = useMemo<string>(() => {
    if (gate.kind === "unlocked") return gate.passedStateCode;
    if (profile?.current_focus_state) return profile.current_focus_state;
    if (profile && profile.target_states.length > 0) return profile.target_states[0];
    return "";
  }, [gate, profile]);

  return (
    <LearnerPortalShell pageTitle={t("whatsnext.pageTitle")}>
      <div className="bg-slate-50">
        {gate.kind === "loading" ? (
          <Loading />
        ) : gate.kind === "error" ? (
          <ErrorBanner message={gate.message} />
        ) : gate.kind === "blocked" ? (
          <Paywall />
        ) : (
          <Journey stateCode={resolvedState} lang={lang} />
        )}
      </div>
    </LearnerPortalShell>
  );
}

function Loading() {
  const { t } = useTranslation();
  return (
    <div
      data-testid="whatsnext-loading"
      role="status"
      className="mx-auto max-w-4xl px-4 py-12 text-neutral-600 sm:px-8"
    >
      {t("whatsnext.loading")}
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  const { t } = useTranslation();
  return (
    <div
      data-testid="whatsnext-error"
      role="alert"
      className="mx-auto max-w-2xl px-4 py-12 sm:px-8"
    >
      <div className="rounded-lg border border-rose-300 bg-rose-50 p-md text-rose-900">
        <p className="font-semibold">{t("whatsnext.error.title")}</p>
        <p className="mt-xs text-sm">
          {t("whatsnext.error.body", { message })}
        </p>
      </div>
    </div>
  );
}

function Paywall() {
  const { t } = useTranslation();
  return (
    <div
      data-testid="whatsnext-paywall"
      className="mx-auto max-w-2xl px-4 py-12 text-center sm:px-8"
      role="region"
      aria-labelledby="whatsnext-paywall-heading"
    >
      <h1
        id="whatsnext-paywall-heading"
        className="font-display text-3xl font-bold tracking-tight text-brand-primary sm:text-4xl"
      >
        {t("whatsnext.paywall.heading")}
      </h1>
      <p className="mt-md text-neutral-700">{t("whatsnext.paywall.body")}</p>
      <Link
        to="/exam"
        data-testid="whatsnext-paywall-cta"
        className="mt-lg inline-flex items-center gap-2 rounded-md bg-brand-primary px-md py-sm text-sm font-semibold text-white no-underline hover:bg-brand-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
      >
        {t("whatsnext.paywall.cta")}
        <span aria-hidden="true">→</span>
      </Link>
    </div>
  );
}

function Journey({ stateCode, lang }: { stateCode: string; lang: Lang }) {
  const { t } = useTranslation();
  return (
    <article
      className="mx-auto flex max-w-4xl flex-col gap-md px-4 py-10 sm:gap-lg sm:px-8 sm:py-14"
      data-testid="whatsnext-journey"
    >
      <header>
        <p className="text-xs font-semibold uppercase tracking-widest text-neutral-600">
          {t("whatsnext.eyebrow")}
        </p>
        <h1
          id="whatsnext-heading"
          className="mt-xs font-display text-3xl font-bold tracking-tight text-brand-primary sm:text-4xl"
        >
          {t("whatsnext.title")}
        </h1>
        <p className="mt-sm text-neutral-700 sm:text-lg">
          {t("whatsnext.subtitle")}
        </p>
      </header>

      <PhysicalLicenceSection stateCode={stateCode} lang={lang} />
      <Insurance101Section stateCode={stateCode} lang={lang} />
      <InsuranceApplyHowToSection />
      <PartnerOffersSection />
      <VehicleRegistrationSection stateCode={stateCode} />
      <AaaMembershipSection />
      <AncillarySection />
    </article>
  );
}
